import React, { useEffect, useState } from 'react';
import EncyclopediaModal from '../components/story/EncyclopediaModal';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useStory } from '../context/StoryContext';
import * as dbService from '../services/dbService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

import Characters from './settings/Characters';
import AITunePage from './AITunePage';

const Dashboard = () => {
  const { user } = useAuth();
  const { startOverStory, loadStoryById } = useStory();
  const navigate = useNavigate();
  const [ownedStories, setOwnedStories] = useState([]);
  const [sharedStories, setSharedStories] = useState([]);
  const [publicStories, setPublicStories] = useState([]);
  const [bookmarkedStories, setBookmarkedStories] = useState([]);
  const [showEncyclopediaPrompt, setShowEncyclopediaPrompt] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [showEncyclopediaModal, setShowEncyclopediaModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stories');
  const [selectedPublicStory, setSelectedPublicStory] = useState(null);
  const [requestStatus, setRequestStatus] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchStories = async () => {
      try {
        const [ownedResult, sharedResult, pubStoriesResult, bookmarksResult] = await Promise.allSettled([
          dbService.getUserStories(user.id),
          dbService.getSharedStories(user.id),
          dbService.getPublicStories(),
          dbService.getUserBookmarks(user.id)
        ]);

        if (ownedResult.status === 'fulfilled') {
          setOwnedStories(ownedResult.value ? ownedResult.value.filter(s => s !== null) : []);
        } else {
          console.error("Failed to load user stories", ownedResult.reason);
          setOwnedStories([]);
        }

        if (sharedResult.status === 'fulfilled') {
          setSharedStories(sharedResult.value ? sharedResult.value.filter(s => s !== null) : []);
        } else {
          console.error("Failed to load shared stories", sharedResult.reason);
          setSharedStories([]);
        }

        if (pubStoriesResult.status === 'fulfilled') {
          setPublicStories(pubStoriesResult.value ? pubStoriesResult.value.filter(s => s !== null) : []);
        } else {
          console.error("Failed to load public stories", pubStoriesResult.reason);
          setPublicStories([]);
        }

        if (bookmarksResult.status === 'fulfilled') {
          setBookmarkedStories(bookmarksResult.value ? bookmarksResult.value.filter(s => s !== null) : []);
        } else {
          console.error("Failed to load bookmarked stories", bookmarksResult.reason);
          setBookmarkedStories([]);
        }
      } catch (error) {
        console.error("Unexpected error in fetchStories", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, [user, navigate]);


  const handleReadClick = async (e, story) => {
    if (e) e.stopPropagation();
    const success = await loadStoryById(story.id);
    if (success) {
      const isOwner = story.owner_id === user?.id;
      const isShared = sharedStories.some(s => s.id === story.id);
      if (isOwner || isShared) {
        navigate('/story');
      } else {
        if (story.encyclopedia && story.encyclopedia.length > 0) {
          setShowEncyclopediaModal(true);
        } else {
          setSelectedStoryId(story.id);
          setShowEncyclopediaPrompt(true);
        }
      }
    }
  };

  const handleToggleBookmark = async (e, storyId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const isBookmarked = await dbService.toggleBookmark(user.id, storyId);
      if (isBookmarked) {
        const story = [...ownedStories, ...sharedStories, ...publicStories].find(s => s.id === storyId);
        if (story) {
           setBookmarkedStories(prev => [...prev, story]);
        }
      } else {
        setBookmarkedStories(prev => prev.filter(s => s.id !== storyId));
      }
    } catch (error) {
      console.error("Failed to toggle bookmark", error);
    }
  };

  const handleStartNew = () => {
    startOverStory();
    navigate('/setup/world');
  };

  const handleDeleteStory = async (e, storyId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this story? This action cannot be undone.")) {
      try {
        await dbService.deleteStory(storyId);
        setOwnedStories(ownedStories.filter(story => story.id !== storyId));
      } catch (error) {
        console.error("Failed to delete story", error);
        alert("Failed to delete story. Please try again.");
      }
    }
  };

  const handleTogglePublic = async (e, storyId, currentStatus) => {
    e.stopPropagation();
    try {
      const updated = await dbService.toggleStoryPublicStatus(storyId, !currentStatus);
      setOwnedStories(ownedStories.map(story => story.id === storyId ? { ...story, is_public: updated.is_public } : story));
    } catch (error) {
      console.error("Failed to toggle public status", error);
      alert("Failed to update story visibility.");
    }
  };

  const handleRequestToJoin = async (storyId) => {
    try {
      setRequestStatus(prev => ({ ...prev, [storyId]: 'requesting' }));
      await dbService.requestToJoinStory(storyId, user.id);
      setRequestStatus(prev => ({ ...prev, [storyId]: 'requested' }));
    } catch (error) {
      console.error("Failed to request to join", error);
      setRequestStatus(prev => ({ ...prev, [storyId]: 'error' }));
      alert("Failed to request to join. You may have already requested.");
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-gray-600 dark:text-white flex justify-center items-center h-full"><SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-amber-500" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-4xl font-serif font-bold text-gray-600 dark:text-white">Your Storyboard</h1>
          <p className="text-gray-600 dark:text-purple-400 mt-2">Manage your adventures and configuration.</p>
        </div>
        {activeTab === 'stories' && (
          <button onClick={handleStartNew} className="btn-primary flex items-center space-x-2">
            <SafeIcon icon={FiIcons.FiPlus} />
            <span>New Story</span>
          </button>
        )}
      </div>

      <div className="flex space-x-4 border-b border-gray-200 dark:border-purple-800 mb-8 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('stories')}
          className={`flex items-center space-x-2 px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'stories'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
              : 'border-transparent text-gray-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-purple-900/30'
          }`}
        >
          <SafeIcon icon={FiIcons.FiBookOpen} />
          <span>Stories</span>
        </button>
        <button
          onClick={() => setActiveTab('public-stories')}
          className={`flex items-center space-x-2 px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'public-stories'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
              : 'border-transparent text-gray-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-purple-900/30'
          }`}
        >
          <SafeIcon icon={FiIcons.FiGlobe} />
          <span>Public Directory</span>
        </button>
        <button
          onClick={() => setActiveTab('bookmarks')}
          className={`flex items-center space-x-2 px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'bookmarks'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
              : 'border-transparent text-gray-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-purple-900/30'
          }`}
        >
          <SafeIcon icon={FiIcons.FiBookmark} />
          <span>Bookmarks</span>
        </button>
        <button
          onClick={() => setActiveTab('characters')}
          className={`flex items-center space-x-2 px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'characters'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
              : 'border-transparent text-gray-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-purple-900/30'
          }`}
        >
          <SafeIcon icon={FiIcons.FiUsers} />
          <span>Characters</span>
        </button>
        <button
          onClick={() => setActiveTab('ai-tune')}
          className={`flex items-center space-x-2 px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'ai-tune'
              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
              : 'border-transparent text-gray-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-purple-900/30'
          }`}
        >
          <SafeIcon icon={FiIcons.FiSliders} />
          <span>AI Tune</span>
        </button>
      </div>

      <div className="space-y-12">
        {activeTab === 'stories' && (
          <>
        <section>
          <h2 className="text-2xl font-serif font-bold text-gray-600 dark:text-purple-100 mb-6 flex items-center space-x-2">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-amber-500" />
            <span>My Stories</span>
          </h2>

          {ownedStories.length === 0 ? (
            <div className="p-8 text-center bg-gray-50 dark:bg-purple-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-purple-800">
              <SafeIcon icon={FiIcons.FiFeather} className="text-4xl text-gray-600 dark:text-purple-300 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-purple-300">You haven't written any stories yet.</p>
              <button onClick={handleStartNew} className="text-amber-600 hover:text-amber-500 font-bold mt-2">Start weaving your first tale &rarr;</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {ownedStories.map(story => (
                <div key={story.id} onClick={(e) => handleReadClick(e, story)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') handleReadClick(e, story); }} className="block group text-left w-full">
                  <motion.div whileHover={{ y: -4 }} className="h-full p-6 bg-gray-900 rounded-2xl border border-purple-800 shadow-sm group-hover:shadow-xl group-hover:border-amber-500 transition-all relative overflow-hidden flex flex-col min-h-[200px]" style={story.background_image ? { backgroundImage: `linear-gradient(to right, rgba(15, 10, 28, 0.9) 0%, rgba(15, 10, 28, 0.7) 40%, rgba(15, 10, 28, 0.1) 100%), url('${story.background_image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                    <div className="flex justify-between items-start mb-2 pr-12 relative z-10">
                      <h3 className="text-2xl font-bold text-white line-clamp-1">{story.title || 'Untitled'}</h3>
                      <div className="absolute top-2 right-2 z-10 flex items-center space-x-2">
                        <button
                           onClick={(e) => handleToggleBookmark(e, story.id)}
                           className={`p-2 rounded-full backdrop-blur-md bg-black/30 transition-colors ${bookmarkedStories.some(b => b.id === story.id) ? 'text-amber-500' : 'text-purple-300 hover:text-amber-500'}`}
                           title={bookmarkedStories.some(b => b.id === story.id) ? "Remove Bookmark" : "Bookmark Story"}
                        >
                           <SafeIcon icon={FiIcons.FiBookmark} className={bookmarkedStories.some(b => b.id === story.id) ? 'fill-current' : ''} />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const success = await loadStoryById(story.id);
                            if (success) navigate('/setup/world');
                          }}
                          className="p-2 rounded-full backdrop-blur-md bg-black/30 text-purple-300 hover:text-purple-400 transition-colors"
                          title="Edit Setup"
                        >
                          <SafeIcon icon={FiIcons.FiSettings} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteStory(e, story.id)}
                          className="p-2 rounded-full backdrop-blur-md bg-black/30 text-purple-300 hover:text-red-400 transition-colors"
                          title="Delete story"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} />
                        </button>
                      </div>
                    </div>
                    <p className="text-base text-purple-300 mb-6 line-clamp-2 max-w-2xl relative z-10">{story.character_name ? `${story.character_name} the ${story.character_role}` : 'No character details.'}</p>

                    {story.collaborators && story.collaborators.length > 0 && (
                      <div className="flex -space-x-2 mb-6 relative z-10">
                        {story.collaborators.slice(0, 3).map((collab, idx) => (
                          <img
    key={idx}
    src={collab.characters?.avatar || `https://ui-avatars.com/api/?name=${collab.profiles?.username || 'User'}&background=8B5CF6&color=fff`}
    alt={collab.profiles?.username || 'User'}
    className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shadow-sm z-10"
    title={`${collab.profiles?.username || 'User'} - ${collab.role} (${collab.status})`}
    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${collab.profiles?.username || 'User'}&background=8B5CF6&color=fff`; }}
  />
                        ))}
                        {story.collaborators.length > 3 && (
                          <div className="w-12 h-12 rounded-full bg-purple-800 text-purple-200 flex items-center justify-center text-xs font-bold border-2 border-white/20 shadow-sm z-0">
                            +{story.collaborators.length - 3}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center space-x-3 mt-auto pt-4 border-t border-purple-900/30 relative z-10">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 ">{story.world_genre || 'Unknown'}</span>
                      <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 ">
                        Ch. {(story.chapters ? story.chapters.length : 0) + 1}
                      </span>
                      <button
                        onClick={(e) => handleTogglePublic(e, story.id, story.is_public)}
                        className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400"
                        title={story.is_public ? "Story is Public" : "Story is Private"}
                      >
                        {story.is_public ? 'Public' : 'Private'}
                      </button>
                      {story.encyclopedia && story.encyclopedia.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const success = loadStoryById(story.id);
                            if (success) navigate('/story?tab=chapters');
                          }}
                          className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 hover:bg-amber-800/80 space-x-1" title="View Lore">
                          <SafeIcon icon={FiIcons.FiBook} />
                          <span>Lore</span>
                        </button>
                      )}
                      <span className="text-xs text-purple-400 ml-auto font-medium">{new Date(story.updated_at).toLocaleDateString()}</span>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-gray-600 dark:text-purple-100 mb-6 flex items-center space-x-2">
            <SafeIcon icon={FiIcons.FiUsers} className="text-blue-500" />
            <span>Shared With Me</span>
          </h2>

          {sharedStories.length === 0 ? (
            <div className="p-8 text-center bg-blue-50/50 dark:bg-blue-900/10 rounded-2xl border border-dashed border-blue-200 dark:border-blue-900/30">
               <SafeIcon icon={FiIcons.FiUsers} className="text-4xl text-blue-300 dark:text-blue-800 mx-auto mb-4" />
               <p className="text-gray-600 dark:text-blue-300">You haven't been invited to collaborate on any stories yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {sharedStories.map(story => (
                <Link key={story.id} to={`/collab/${story.id}`} className="block group">
                  <motion.div whileHover={{ y: -4 }} className="h-full p-6 bg-gray-900 rounded-2xl border border-blue-900/50 shadow-sm group-hover:shadow-xl group-hover:border-blue-500 transition-all relative overflow-hidden flex flex-col min-h-[200px]" style={story.background_image ? { backgroundImage: `linear-gradient(to right, rgba(15, 10, 28, 0.9) 0%, rgba(15, 10, 28, 0.7) 40%, rgba(15, 10, 28, 0.1) 100%), url('${story.background_image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                    <div className="absolute top-4 right-4 z-10">
                       <button
                           onClick={(e) => { e.preventDefault(); handleToggleBookmark(e, story.id); }}
                           className={`p-2 rounded-full backdrop-blur-md bg-black/30 transition-colors ${bookmarkedStories.some(b => b.id === story.id) ? 'text-amber-500' : 'text-purple-300 hover:text-amber-500'}`}
                           title={bookmarkedStories.some(b => b.id === story.id) ? "Remove Bookmark" : "Bookmark Story"}
                        >
                           <SafeIcon icon={FiIcons.FiBookmark} className={bookmarkedStories.some(b => b.id === story.id) ? 'fill-current' : ''} />
                        </button>
                    </div>
                    {story.userStatus === 'Invited' && (
                      <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl shadow-sm z-10">NEW INVITE</div>
                    )}
                    <h3 className="text-2xl font-bold text-white mb-4 line-clamp-1 pr-8 relative z-10">{story.title || 'Untitled'}</h3>
                    <div className="flex items-center space-x-2 mb-6 relative z-10">
                      <span className="text-base text-purple-400">Owner:</span>
                      <div className="flex items-center space-x-2">
                        <img
    src={story.character_avatar || `https://ui-avatars.com/api/?name=${story.profiles?.username || 'U'}&background=8B5CF6&color=fff`}
    alt={story.profiles?.username || 'User'}
    className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shadow-sm"
    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${story.profiles?.username || 'User'}&background=8B5CF6&color=fff`; }}
  />
                        <span className="text-base font-medium text-purple-200 line-clamp-1">
                          {story.profiles?.username || 'Unknown User'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 mt-auto pt-4 border-t border-white/20 relative z-10">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md border-purple-800/50 bg-purple-900/60 text-purple-300 space-x-1">
                        <SafeIcon icon={FiIcons.FiShield} />
                        <span>{story.userRole}</span>
                      </span>
                      {story.encyclopedia && story.encyclopedia.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const success = loadStoryById(story.id);
                            if (success) navigate('/story?tab=chapters');
                          }}
                          className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 hover:bg-amber-800/80 space-x-1" title="View Lore">
                          <SafeIcon icon={FiIcons.FiBook} />
                          <span>Lore</span>
                        </button>
                      )}
                      <span className="text-xs text-purple-400 ml-auto font-medium">{new Date(story.updated_at).toLocaleDateString()}</span>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          )}
        </section>
          </>
        )}

        {activeTab === 'public-stories' && (
          <section>
            <h2 className="text-2xl font-serif font-bold text-gray-600 dark:text-purple-100 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiGlobe} className="text-green-500" />
              <span>Public Directory</span>
            </h2>

            {publicStories.length === 0 ? (
              <div className="p-8 text-center bg-green-50/50 dark:bg-green-900/10 rounded-2xl border border-dashed border-green-200 dark:border-green-900/30">
                 <SafeIcon icon={FiIcons.FiGlobe} className="text-4xl text-green-300 dark:text-green-800 mx-auto mb-4" />
                 <p className="text-gray-600 dark:text-green-300">No public stories available yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {publicStories.map(story => {
                  const isOwner = story.owner_id === user?.id;
                  const reqStatus = requestStatus[story.id];
                  return (
                    <div key={story.id} className="block group">
                      <motion.div whileHover={{ y: -4 }} className="h-full p-6 bg-gray-900 rounded-2xl border border-green-900/50 shadow-sm group-hover:shadow-xl group-hover:border-green-500 transition-all relative overflow-hidden flex flex-col min-h-[200px]" style={story.background_image ? { backgroundImage: `linear-gradient(to right, rgba(15, 10, 28, 0.9) 0%, rgba(15, 10, 28, 0.7) 40%, rgba(15, 10, 28, 0.1) 100%), url('${story.background_image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                        <div className="absolute top-4 right-4 z-10">
                           <button
                               onClick={(e) => handleToggleBookmark(e, story.id)}
                               className={`p-2 rounded-full backdrop-blur-md bg-black/30 transition-colors ${bookmarkedStories.some(b => b.id === story.id) ? 'text-amber-500' : 'text-purple-300 hover:text-amber-500'}`}
                               title={bookmarkedStories.some(b => b.id === story.id) ? "Remove Bookmark" : "Bookmark Story"}
                            >
                               <SafeIcon icon={FiIcons.FiBookmark} className={bookmarkedStories.some(b => b.id === story.id) ? 'fill-current' : ''} />
                            </button>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4 line-clamp-1 pr-8 relative z-10">{story.title || 'Untitled'}</h3>
                        <div className="flex items-center space-x-2 mb-4 relative z-10">
                          <span className="text-base text-purple-400">Owner:</span>
                          <span className="text-base font-medium text-purple-200 line-clamp-1">
                            {story.profiles?.username || 'Unknown User'}
                          </span>
                        </div>
                        <div className="text-base text-purple-300 mb-6 line-clamp-2 max-w-2xl relative z-10">
                          {story.world_setting || story.world_genre || 'No setting details.'}
                        </div>

                        <div className="mt-auto pt-4 border-t border-purple-900/30 flex items-center space-x-3 relative z-10">
                             <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 ">
                               Ch. {(story.chapters ? story.chapters.length : 0) + 1}
                             </span>
                             {story.encyclopedia && story.encyclopedia.length > 0 && (
                               <button
                                 onClick={(e) => { e.stopPropagation(); setShowEncyclopediaModal(true); }}
                                 className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 hover:bg-amber-800/80 space-x-1"
                               >
                                 <SafeIcon icon={FiIcons.FiBook} />
                                 <span>Lore</span>
                               </button>
                             )}
                             {!isOwner && (
                               <button
                                 onClick={() => handleRequestToJoin(story.id)}
                                 disabled={reqStatus === 'requesting' || reqStatus === 'requested'}
                                 className={`px-4 py-1.5 ml-auto text-xs rounded-full font-bold transition-colors shadow-md ${
                                   reqStatus === 'requested'
                                     ? 'bg-gray-800/60 backdrop-blur-md text-gray-500 cursor-not-allowed'
                                     : 'bg-amber-600 text-white hover:bg-amber-700'
                                 }`}
                               >
                                 {reqStatus === 'requesting' ? 'Requesting...' : reqStatus === 'requested' ? 'Requested' : 'Request to Join'}
                               </button>
                             )}
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {activeTab === 'bookmarks' && (
          <section>
            <h2 className="text-2xl font-serif font-bold text-gray-600 dark:text-purple-100 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiBookmark} className="text-amber-500" />
              <span>Bookmarked Stories</span>
            </h2>

            {bookmarkedStories.length === 0 ? (
              <div className="p-8 text-center bg-gray-50 dark:bg-purple-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-purple-800">
                <SafeIcon icon={FiIcons.FiBookmark} className="text-4xl text-gray-600 dark:text-purple-300 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-purple-300">You haven't bookmarked any stories yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {bookmarkedStories.map(story => (
                  <div key={story.id} className="block group">
                    <motion.div whileHover={{ y: -4 }} className="h-full p-6 bg-gray-900 rounded-2xl border border-purple-800 shadow-sm group-hover:shadow-xl group-hover:border-amber-500 transition-all relative overflow-hidden flex flex-col min-h-[200px]" style={story.background_image ? { backgroundImage: `linear-gradient(to right, rgba(15, 10, 28, 0.9) 0%, rgba(15, 10, 28, 0.7) 40%, rgba(15, 10, 28, 0.1) 100%), url('${story.background_image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                       <div className="absolute top-4 right-4 z-10">
                           <button
                               onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleBookmark(e, story.id); }}
                               className="p-2 rounded-full backdrop-blur-md bg-black/30 text-amber-500 hover:text-amber-600 transition-colors"
                               title="Remove Bookmark"
                            >
                               <SafeIcon icon={FiIcons.FiBookmark} className="fill-current" />
                            </button>
                        </div>
                      <h3 className="text-2xl font-bold text-white mb-4 line-clamp-1 pr-8 relative z-10">{story.title || 'Untitled'}</h3>
                      <div className="flex items-center space-x-2 mb-6 relative z-10">
                        <span className="text-base text-purple-400">Owner:</span>
                        <span className="text-base font-medium text-purple-200 line-clamp-1">
                          {story.profiles?.username || 'Unknown User'}
                        </span>
                      </div>
                      <div className="text-base text-purple-300 mb-6 line-clamp-2 max-w-2xl relative z-10">
                        {story.world_setting || story.world_genre || 'No setting details.'}
                      </div>

                      <div className="mt-auto pt-4 border-t border-purple-900/30 flex items-center space-x-3 relative z-10">
                           <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 ">{story.world_genre || 'Unknown'}</span>
                           <span className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 ">
                             Ch. {(story.chapters ? story.chapters.length : 0) + 1}
                           </span>
                           {story.encyclopedia && story.encyclopedia.length > 0 && (
                             <button
                               onClick={(e) => { e.stopPropagation(); setShowEncyclopediaModal(true); }}
                               className="inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border transition-colors backdrop-blur-md bg-amber-900/60 border-amber-800/50 text-amber-400 hover:bg-amber-800/80 space-x-1" title="View Lore"
                             >
                               <SafeIcon icon={FiIcons.FiBook} />
                               <span>Lore</span>
                             </button>
                           )}
                           <button
                             onClick={(e) => handleReadClick(e, story)}
                             className="px-6 py-2 text-xs rounded-full font-bold transition-colors bg-purple-600 text-white hover:bg-purple-700 shadow-md ml-auto"
                           >
                             Read
                           </button>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'characters' && (
          <div className="-mx-4 sm:mx-0">
            <Characters />
          </div>
        )}

        {activeTab === 'ai-tune' && (
          <div className="-mx-4 sm:mx-0">
            <AITunePage />
          </div>
        )}

      {showEncyclopediaPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-purple-950 p-8 rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 dark:border-purple-800 text-center">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Lore is Empty</h3>
            <p className="text-gray-600 dark:text-purple-300 mb-8">The owner of this story hasn't written any lore entries in the encyclopedia yet.</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setShowEncyclopediaPrompt(false);
                  navigate('/story');
                }}
                className="px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-purple-900 dark:text-white dark:hover:bg-purple-800 transition-colors"
              >
                Go to Narrative
              </button>
              <button
                onClick={() => setShowEncyclopediaPrompt(false)}
                className="px-6 py-3 rounded-xl font-bold bg-amber-600 text-white hover:bg-amber-500 shadow-lg shadow-amber-900/20 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showEncyclopediaModal && (
         <EncyclopediaModal isOpen={showEncyclopediaModal} onClose={() => setShowEncyclopediaModal(false)} />
      )}

      </div>
    </div>
  );
};

export default Dashboard;
